import { StyleSheet, css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { CalendarType } from '../../../containers/ContentCalendar/models';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogBackdrop,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogPortal,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { MASK_PRESETS, MaskedInput } from '../../../components-v2/MaskedInput';
import { THEME } from '../../../components-v2/Theme/theme';
import { buildTimeSpansFromStart } from './models';
import { formatCalendarRange } from './presentation';

const s = StyleSheet.create({
	/** Spacing below the input so the date preview below it has room to breathe. */
	maskedInput: {
		marginBottom: THEME.spacing.md,
	},
	/**
	 * Date preview label — a smaller echo of the calendar title.
	 *
	 * `fontSize` uses the THEME `xl` (24px) token. `fontFamily` is intentionally the literal
	 * 'Lexend-Regular' — NOT `THEME.typography.fontFamilies.lexend` (a different 'Lexend,
	 * OpenSans' stack) — so the preview matches the calendar title's font exactly.
	 */
	datePreview: {
		margin: 0,
		fontFamily: 'Lexend-Regular, sans-serif',
		fontSize: THEME.typography.fontSizes.xl,
	},
});

/**
 * Strict parse format for the typed value. We parse here rather than relying solely on
 * MaskedInput's `change.isValid` because we need the resulting moment for both the live
 * preview and `onApply` — one strict parse yields both `parsed` and `isValid`.
 */
const DATE_FORMAT = 'MM/DD/YYYY';

/** Props for the JumpToDateDialog component. */
export interface IJumpToDateDialogProps {
	/** Whether the dialog is open. */
	open: boolean;
	/** Called when the dialog open state changes. */
	onOpenChange(open: boolean): void;
	/** The current calendar view type (Month, Quarterly, or Weekly). */
	calendarType: CalendarType;
	/** How many months the calendar currently shows. Used for the preview range. */
	numberOfMonthsToShow: number;
	/** The calendar's current date label, shown until a valid date is typed. */
	currentLabel: string;
	/**
	 * Called with the typed date (un-snapped) when the user clicks Apply.
	 * The parent is responsible for snapping the date and jumping the calendar.
	 */
	onApply(date: moment.Moment): void;
}

/**
 * A dialog that lets the user type a date and jump the content calendar to that date.
 * Always shows a date label — defaults to `currentLabel` until a valid date is typed,
 * then shows the computed new calendar range/title.
 */
export const JumpToDateDialog = ({
	open,
	onOpenChange,
	calendarType,
	numberOfMonthsToShow,
	currentLabel,
	onApply,
}: IJumpToDateDialogProps) => {
	const [value, setValue] = React.useState('');

	// Reset input value whenever the dialog transitions from closed to open.
	const wasOpen = React.useRef(false);
	React.useEffect(() => {
		if (open && !wasOpen.current) {
			setValue('');
		}
		wasOpen.current = open;
	}, [open]);

	const parsed = moment(value, DATE_FORMAT, true);
	const isValid = parsed.isValid();

	// Compute the new label from the typed date for all view types.
	// Falls back to the current calendar label when no valid date has been entered yet.
	const newLabel = isValid
		? (() => {
				const spans = buildTimeSpansFromStart(calendarType, numberOfMonthsToShow, parsed);
				const endMoment = calendarType === CalendarType.Month ? null : spans[spans.length - 1].moment;
				return formatCalendarRange(spans[0].moment, endMoment, calendarType);
			})()
		: null;
	const displayLabel = newLabel ?? currentLabel;

	const apply = () => {
		if (!isValid) {
			return;
		}
		onApply(parsed);
		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPortal>
				<DialogBackdrop />
				<DialogPopup showCloseButton={false}>
					<DialogHeader>
						<DialogTitle>Jump to date</DialogTitle>
					</DialogHeader>
					<DialogBody>
						<MaskedInput
							{...MASK_PRESETS.dateMDY}
							styles={[s.maskedInput]}
							value={value}
							onValueChange={change => setValue(change.formatted)}
						/>
						<p className={css(s.datePreview)}>{displayLabel}</p>
					</DialogBody>
					<DialogFooter>
						<Button type='button' variant='ghost' onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button type='button' disabled={!isValid} onClick={apply}>
							Apply
						</Button>
					</DialogFooter>
				</DialogPopup>
			</DialogPortal>
		</Dialog>
	);
};
